import { Context, useMemo, useContext, useCallback } from "react"
import { chain, filter } from "lodash"
import moment from "moment"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createExposureMultiDateQuery } from "gatherers/multi-date/exposure"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import { computeFilteredSegment } from "helpers/segments"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { getFactorLabel } from "helpers/factors"
import { numberFormatter } from "helpers/formatters/numbers"
import { ModelMetadata } from "resources/models"
import { GraphContextValue } from "connected-components/graph/types"
import { useExposureContext } from "./context"

interface ExposureItem {
  id: string
  label: string
  category: string
  disabled: boolean
  type: "factor"
}

interface ExposureDataPoint {
  date: moment.Moment
  [key: string]: unknown
}

function createExposureItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean | undefined>,
): ExposureItem[] {
  if (!modelMetadata?.factors) return []

  const factorsByIdMap = chain(modelMetadata.factors).keyBy("id").value()

  const factors = selectedCategory === "total"
    ? modelMetadata.factors
    : filter(modelMetadata.factors, factor => factor.category === selectedCategory)

  return factors.map(factor => ({
    id: factor.id,
    label: getFactorLabel(factorsByIdMap, factor.id),
    category: factor.category,
    disabled: Boolean(disabledItems[factor.id]),
    type: "factor" as const,
  }))
}

interface ExposureGraphProps {
  modelMetadata: ModelMetadata
  context: Context<GraphContextValue>
}

export default function ExposureGraph({ modelMetadata, context }: ExposureGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter: portfolioFilter,
    normalization,
    instrument,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
  } = useContext(context)

  const { selectedCategory } = useExposureContext()

  const baseWithSegment = useBaseWithSegment(base ?? null, portfolioFilter ?? undefined, normalization)
  const segment = useMemo(
    () => computeFilteredSegment(portfolioFilter, normalization),
    [portfolioFilter, normalization],
  )
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors()
  const currentItems = useMemo(
    () => createExposureItems(modelMetadata, selectedCategory, disabledItems),
    [modelMetadata, selectedCategory, disabledItems],
  )

  const filterItems = useCallback(
    (items: ExposureItem[], data: ExposureDataPoint[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => [
    // TODO: [INTRADAY-BATCH] replace with intraday batch data when isIntradayEnabled
    createExposureMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
    ),
  ], [modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate])

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      formatter: numberFormatter,
      loadingMessage: "Loading exposure data...",
      errorMessage: "Failed to load exposure data. Please try again.",
      emptyMessage: "No exposure data available.",
    }}
  />
}
